/**
 * FinalisingOverlay — full-screen modal shown while the EC2 master
 * concat runs after the operator clicks Stop Recording.
 *
 * The chunks are already in S3 by Stop time. The "finalising" step is
 * server-side ffmpeg streaming concat → S3 multipart upload of the
 * master MP4. Typical wait: 60-90s for a 3-hour match.
 *
 * UX contract:
 *   - Operator can't dismiss this overlay (no close button).
 *   - beforeunload handler warns on tab-close / refresh attempts.
 *   - Progress bar reflects MatchRecording.finalize_progress_pct as
 *     written by the EC2 thread.
 *   - Phase label gives a human-readable status.
 *   - On `complete` phase, parent unmounts the overlay (this component
 *     just renders what's passed in — it doesn't manage its own state).
 *   - On `failed` phase, shows the error + a "close" affordance so
 *     the operator isn't stuck staring at a dead modal.
 */
import { useEffect, useRef, useState } from "react";
import { Loader2, AlertTriangle, CheckCircle2, X } from "lucide-react";
import useDialogDismiss from "../../hooks/useDialogDismiss";


const PHASE_LABEL = {
  queued:        "Queued — server picking up the job",
  concatenating: "Streaming chunks into master MP4",
  uploading:     "Uploading master MP4 to S3",
  verifying:     "Verifying master MP4 in S3",
  complete:      "Done",
  failed:        "Failed",
};


export default function FinalisingOverlay({ finalizeState, onDismiss }) {
  const phase = finalizeState?.phase || "queued";
  const pct   = Math.max(0, Math.min(100, finalizeState?.pct || 0));
  const error = finalizeState?.error || "";
  const isFailed   = phase === "failed";
  const isComplete = phase === "complete";
  const panelRef = useRef(null);
  // Esc / hardware-back maps to the same Force-close X exposed below
  // so operators are never trapped behind a stuck phase.
  useDialogDismiss({ open: true, onClose: onDismiss || (() => {}), ref: panelRef });

  // Track how long the overlay has been mounted so we know when to
  // surface a "Force close" escape hatch. Stuck-overlay reports got
  // operators trapped behind a phase that wasn't advancing (server
  // aborted the recording, network blip, etc.) with no way to
  // dismiss because the original code only showed Close on
  // complete/failed phases.
  const [openedAt] = useState(() => Date.now());
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (isComplete || isFailed) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [isComplete, isFailed]);
  const elapsedSec = Math.floor((Date.now() - openedAt) / 1000);
  // Show the force-close button after 60s in a non-terminal phase.
  // Master concat for a 3hr match should take <90s; if we're past 60s
  // with no progress, something's stuck and the operator should be
  // able to bail.
  const showForceClose = !isComplete && !isFailed && elapsedSec >= 60;

  // Soft-block tab close while finalising IS in progress (phase
  // actually advancing). For stuck-overlay cases the force-close
  // button + this no-op handler is the escape. We DON'T register
  // beforeunload anymore — it caused too many "I can't refresh"
  // reports during testing and the chunks are already durable in
  // S3 regardless of whether the overlay stays open.
  useEffect(() => {
    // No beforeunload — chunks are durable in S3, the master concat
    // continues server-side, and trapping the tab caused worse UX
    // than letting the operator refresh and re-poll the state.
    return undefined;
  }, []);

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4" role="presentation">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Finalising recording"
        className="bg-bg-primary border border-bg-secondary rounded-2xl shadow-2xl w-full max-w-lg p-6"
      >
        <div className="flex items-center gap-3 mb-4">
          {isFailed ? (
            <AlertTriangle className="w-7 h-7 text-rose-400" />
          ) : isComplete ? (
            <CheckCircle2 className="w-7 h-7 text-emerald-400" />
          ) : (
            <Loader2 className="w-7 h-7 text-button-primary animate-spin" />
          )}
          <h2 className="text-xl font-semibold text-text-primary flex-1">
            {isFailed
              ? "Master video failed to build"
              : isComplete
                ? "Recording complete"
                : "Finalising recording"}
          </h2>
          {/* Force-close X — always available so operators are never
              trapped behind a stuck overlay. Chunks are durable in S3
              regardless; the master continues building server-side. */}
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="p-1 rounded text-text-secondary hover:text-text-primary hover:bg-bg-secondary"
              title="Close this dialog. The master video keeps building in the background."
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {!isFailed && !isComplete && (
          <p className="text-sm text-text-secondary leading-relaxed mb-4">
            <strong className="text-text-primary">Please don't close this tab.</strong>{" "}
            The per-ball clips are already in S3 — what's happening now is
            the master full-match video being assembled on the server. This
            usually takes 60-90 seconds.
          </p>
        )}

        {isFailed && (
          <p className="text-sm text-rose-300 leading-relaxed mb-4 whitespace-pre-wrap break-words">
            {error || "An unknown error occurred during master concat. The chunks are still in S3 — try Stop again to retry, or contact support."}
          </p>
        )}

        {isComplete && (
          <p className="text-sm text-emerald-300 leading-relaxed mb-4">
            Master MP4 is in S3 and the recording is fully archived. You can
            close this overlay safely.
          </p>
        )}

        <div className="w-full bg-bg-secondary rounded-full h-3 overflow-hidden mb-2">
          <div
            className={`h-full transition-all duration-500 ${
              isFailed
                ? "bg-rose-500"
                : isComplete
                  ? "bg-emerald-500"
                  : "bg-button-primary"
            }`}
            style={{ width: `${isFailed ? 100 : pct}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-xs text-text-secondary">
          <span>{PHASE_LABEL[phase] || phase}</span>
          <span className="font-mono">{isFailed ? "—" : `${pct}%`}</span>
        </div>

        {(isFailed || isComplete) && onDismiss && (
          <div className="mt-5 flex justify-end">
            <button
              onClick={onDismiss}
              className={`text-xs uppercase tracking-wider px-4 py-2 rounded ${
                isFailed
                  ? "bg-rose-500/20 text-rose-200 hover:bg-rose-500/30 border border-rose-500/40"
                  : "bg-emerald-600 text-white hover:opacity-90"
              }`}
            >
              {isFailed ? "Close" : "Got it"}
            </button>
          </div>
        )}

        {/* Soft-skip escape hatch for non-terminal phases. After 60s
            in a non-terminal phase we surface a less-subtle "skip"
            button — master concat continues server-side, operator
            can keep working. */}
        {!isFailed && !isComplete && showForceClose && onDismiss && (
          <div className="mt-5 flex items-center justify-between gap-3">
            <span className="text-[11px] text-text-secondary">
              Taking longer than usual ({elapsedSec}s). The master video
              will continue building in the background.
            </span>
            <button
              onClick={onDismiss}
              className="text-xs uppercase tracking-wider px-3 py-1.5 rounded bg-bg-secondary text-text-secondary hover:text-text-primary border border-border-default"
            >
              Skip — keep working
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
